import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { PaymentIframeModalComponent } from 'src/app/shared/components/payment-iframe-modal/payment-iframe-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import {
  PaymentOrderType,
  buildPaymentResultUrl,
  getPaymentRemainingMs,
  readPaymentSessionSnapshot,
  storePaymentSessionSnapshot
} from 'src/app/shared/utils/payment-flow.util';
import { ConstantUtil } from 'src/app/util/constant.util';

type BalanceType = 'uupon';
type PointPurchaseTab = 'purchase' | 'unpaid';

interface PointPackagePurchaseOption {
  pricingConfigId: string;
  pricingItemId: string;
  pricingCode: string;
  pricingType?: string | null;
  recommend?: boolean | null;
  title: string;
  description: string;
  basePoints: number;
  bonusPoints: number;
  displayOrder: number;
  priceCny: number;
  addonValue?: number;
}

interface BalanceCard {
  type: BalanceType;
  points: number;
  deductRate: number;
}

interface PointPackagePurchaseModalData {
  tenantId?: string | null;
  aipoolTenantId?: string | null;
  pricingCode?: string | null;
  options?: Array<Partial<PointPackagePurchaseOption>> | null;
  productType?: 'point' | 'numeric-addon';
  quotaName?: string | null;
  unit?: string | null;
}

interface PointUnpaidOrderSnapshot {
  orderId?: string | null;
  pricingConfigId?: string | null;
  pricingItemId?: string | null;
  pricingCode?: string | null;
  status?: string | number | null;
  latestOrderStatus?: string | number | null;
  orderStatus?: string | number | null;
  paymentStatus?: string | number | null;
  payableCash?: number | null;
  priceCny?: number | null;
  payStartTime?: number | null;
  payExpireTime?: number | null;
  reserveTimeoutSeconds?: number | null;
  paymentUrl?: string | null;
  payUrl?: string | null;
  needPay?: boolean | null;
  payRequired?: boolean | null;
}

@Component({
  selector: 'point-package-purchase-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzEmptyModule, NzIconModule, NzModalModule, NzSpinModule, I18nPipe, TestIdDirective],
  templateUrl: './point-package-purchase-modal.component.html',
  styleUrls: ['./point-package-purchase-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PointPackagePurchaseModalComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject(NzModalRef);
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: PointPackagePurchaseModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  activeTab: PointPurchaseTab = 'purchase';
  submitting = false;
  options: PointPackagePurchaseOption[] = [];
  selectedOption: PointPackagePurchaseOption | null = null;
  balanceCards: BalanceCard[] = [];
  deductionInputs: Record<BalanceType, number | null> = {
    uupon: null
  };
  unpaidOrder: PointUnpaidOrderSnapshot | null = null;
  private aipoolTenantId = '';

  get isNumericAddon(): boolean {
    return this.modalData.productType === 'numeric-addon';
  }

  get productTitle(): string {
    return this.isNumericAddon ? String(this.modalData.quotaName ?? '').trim() : this.t('quota.view.point-package.modal.title');
  }

  get productSubtitle(): string {
    return this.isNumericAddon ? this.t('quota.view.numeric-addon.modal.subtitle') : this.t('quota.view.point-package.modal.subtitle');
  }

  get productUnit(): string {
    return String(this.modalData.unit ?? '').trim();
  }

  private orderEndpoint(kind: 'create' | 'pay' | 'item' | 'pending' | 'cancel'): string {
    if (!this.isNumericAddon) {
      return {
        create: ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_CREATE,
        pay: ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_PAY,
        item: ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_ITEM,
        pending: ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_PENDING,
        cancel: ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_CANCEL
      }[kind];
    }
    return {
      create: ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_CREATE,
      pay: ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_PAY,
      item: ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_ITEM,
      pending: ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_PENDING,
      cancel: ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_CANCEL
    }[kind];
  }

  ngOnInit(): void {
    this.options = this.normalizeOptions(this.modalData.options);
    this.selectedOption = this.options[0] ?? null;
    this.aipoolTenantId = String(this.modalData.aipoolTenantId ?? '').trim();
    this.loadBalanceSummary();
    this.loadExistingUnpaidOrder();
  }

  t(key: string, params?: Record<string, unknown>): string {
    let text = String(this.i18n.fanyi(key, params) ?? '');
    Object.entries(params ?? {}).forEach(([paramKey, value]) => {
      const safeValue = String(value ?? '');
      text = text.replace(new RegExp(`\\{\\{\\s*${paramKey}\\s*\\}\\}`, 'g'), safeValue);
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), safeValue);
    });
    return text;
  }

  get hasOptions(): boolean {
    return this.options.length > 0;
  }

  get availableTabs(): Array<{ key: PointPurchaseTab; label: string }> {
    const tabs: Array<{ key: PointPurchaseTab; label: string }> = [
      {
        key: 'purchase',
        label: this.isNumericAddon
          ? this.t('quota.view.numeric-addon.modal.tab-purchase')
          : this.t('quota.view.point-package.modal.tab-purchase')
      }
    ];
    if (this.hasUnpaidOrder) {
      tabs.push({ key: 'unpaid', label: this.t('quota.view.point-package.modal.tab-unpaid') });
    }
    return tabs;
  }

  get hasUnpaidOrder(): boolean {
    return !!this.unpaidOrder && !this.isOrderClosed(this.unpaidOrder);
  }

  get showPurchaseTab(): boolean {
    return this.activeTab === 'purchase';
  }

  get showUnpaidTab(): boolean {
    return this.activeTab === 'unpaid' && this.hasUnpaidOrder;
  }

  get isUnpaidOrderExpired(): boolean {
    const remainingMs = this.getUnpaidOrderRemainingMs();
    return remainingMs !== null && remainingMs <= 0;
  }

  get unpaidOrderActionLabel(): string {
    return this.isUnpaidOrderExpired
      ? this.t('quota.view.point-package.modal.cancel-unpaid')
      : this.t('quota.view.point-package.modal.continue-pay');
  }

  get unpaidOrderHint(): string {
    return this.isUnpaidOrderExpired
      ? this.t('quota.view.point-package.modal.unpaid-expired-hint')
      : this.t('quota.view.point-package.modal.unpaid-hint');
  }

  get purchaseHint(): string {
    if (!this.hasUnpaidOrder) {
      return '';
    }
    return this.isUnpaidOrderExpired
      ? this.t('quota.view.point-package.modal.purchase-hint-expired')
      : this.t('quota.view.point-package.modal.purchase-hint-pending');
  }

  get unpaidOrderStatusText(): string {
    return this.isUnpaidOrderExpired
      ? this.t('quota.view.point-package.modal.unpaid-status-expired')
      : this.t('quota.view.point-package.modal.unpaid-status-pending');
  }

  get unpaidOrderPriceText(): string {
    return this.formatCurrency(this.unpaidOrder?.payableCash ?? this.unpaidOrder?.priceCny ?? 0);
  }

  get unpaidOrderCreateTimeText(): string {
    return this.formatTimestamp(this.unpaidOrder?.payStartTime);
  }

  get unpaidOrderExpireTimeText(): string {
    return this.formatTimestamp(this.unpaidOrder?.payExpireTime);
  }

  get canCreateNewOrder(): boolean {
    return !this.hasUnpaidOrder;
  }

  selectOverviewTab(tab: PointPurchaseTab): void {
    if (tab === 'unpaid' && !this.hasUnpaidOrder) {
      this.activeTab = 'purchase';
      return;
    }
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  selectOption(option: PointPackagePurchaseOption): void {
    if (this.hasUnpaidOrder) {
      return;
    }
    this.selectedOption = option;
    this.deductionInputs = { uupon: null };
  }

  formatNumber(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '--';
    return new Intl.NumberFormat(this.i18n.currentLang || undefined).format(Number(value || 0));
  }

  formatCurrency(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '--';
    return `${this.t('pricing.package.currency.twd')} ${this.formatNumber(value)}`;
  }

  getTotalPoints(option: PointPackagePurchaseOption | null): number {
    return Number(option?.basePoints ?? 0) + Number(option?.bonusPoints ?? 0);
  }

  getSelectedValue(option: PointPackagePurchaseOption | null): number {
    return this.isNumericAddon ? Number((option as any)?.addonValue ?? option?.basePoints ?? 0) : this.getTotalPoints(option);
  }

  getOptionDescription(option: PointPackagePurchaseOption | null): string {
    return String(option?.description ?? '').trim();
  }

  getPointsBreakdown(option: PointPackagePurchaseOption | null): string {
    const basePoints = Number(option?.basePoints ?? 0);
    const bonusPoints = Number(option?.bonusPoints ?? 0);
    if (basePoints <= 0 && bonusPoints <= 0) return '';
    if (bonusPoints > 0) {
      return `${this.formatNumber(basePoints)} + ${this.formatNumber(bonusPoints)}`;
    }
    return this.formatNumber(basePoints);
  }

  getSelectedPrice(): number {
    if (this.hasUnpaidOrder) {
      return Math.max(0, Number(this.unpaidOrder?.payableCash ?? this.unpaidOrder?.priceCny ?? 0) || 0);
    }
    return Math.max(0, Number(this.selectedOption?.priceCny ?? 0) || 0);
  }

  getDeductPoints(type: BalanceType): number {
    return this.toNonNegativeInteger(this.deductionInputs[type]);
  }

  getDeductedCash(type: BalanceType): number {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return 0;
    const points = this.getDeductPoints(type);
    return points > 0 ? points / card.deductRate : 0;
  }

  getPayableCash(): number {
    return Math.max(0, Number((this.getSelectedPrice() - this.getDeductedCash('uupon')).toFixed(2)));
  }

  getBalanceTypeLabel(_type: BalanceType): string {
    return this.t('quota.view.point-package.modal.deduct-uupon');
  }

  getBalancePointsLabel(points: number): string {
    return this.t('quota.view.point-package.modal.balance-points', { points: this.formatNumber(points) });
  }

  getUnpaidOrderBalancePointsLabel(points: number): string {
    return this.t('quota.view.point-package.modal.balance-points-short', { points: this.formatNumber(points) });
  }

  getDeductRateText(rate: number): string {
    return this.t('quota.view.point-package.modal.deduct-rate', { rate });
  }

  getDeductAmountText(type: BalanceType): string {
    return this.t('quota.view.point-package.modal.deduct-amount', { amount: this.formatCurrency(this.getDeductedCash(type)) });
  }

  onDeductInput(type: BalanceType, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const integerText = input.value.replace(/\D+/g, '');
    this.applyDeductInput(type, integerText);
    input.value = this.deductionInputs[type] === null ? '' : String(this.deductionInputs[type]);
  }

  blockNonIntegerInput(event: InputEvent): void {
    if (event.data && !/^\d+$/.test(event.data)) {
      event.preventDefault();
    }
  }

  applyDeductInput(type: BalanceType, value: string | number | null | undefined): void {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const raw = Number(value ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.deductionInputs[type] = null;
      return;
    }
    const packagePrice = this.getSelectedPrice();
    if (raw / card.deductRate >= packagePrice) {
      const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
      const points = Math.min(card.points, maxPointsByPackage);
      this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
      return;
    }
    const maxByBalance = card.points;
    const maxByPrice = Math.floor(packagePrice * card.deductRate);
    const normalized = Math.floor(Math.min(raw, maxByBalance, maxByPrice));
    this.deductionInputs[type] = normalized > 0 ? normalized : null;
  }

  deductAll(type: BalanceType): void {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const packagePrice = this.getSelectedPrice();
    const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
    const points = Math.min(card.points, maxPointsByPackage);
    this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
  }

  close(): void {
    this.modalRef.close();
  }

  confirmPay(): void {
    if (this.submitting) return;
    if (this.hasUnpaidOrder) {
      if (this.isUnpaidOrderExpired) {
        this.cancelUnpaidOrder();
      } else {
        this.continueUnpaidOrderPayment();
      }
      return;
    }
    if (!this.validateUUPonMultiple()) return;
    if (!this.selectedOption) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-select-plan'));
      return;
    }
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    if (!tenantId) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-tenant-missing'));
      return;
    }
    const aipoolTenantId = String(this.aipoolTenantId || this.modalData.aipoolTenantId || '').trim();
    if (!aipoolTenantId) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-aipool-missing'));
      return;
    }
    this.aipoolTenantId = aipoolTenantId;
    this.submitting = true;
    let handOffToPay = false;
    this.http
      .post(this.orderEndpoint('create'), {
        aipoolTenantId,
        pricingConfigId: this.selectedOption.pricingConfigId,
        pricingCode: this.selectedOption.pricingCode,
        quotaCode: this.isNumericAddon ? this.selectedOption.pricingCode : undefined,
        pricingItemId: this.selectedOption.pricingItemId,
        deductUUPonPoints: this.getDeductPoints('uupon'),
        payableCash: this.getPayableCash()
      })
      .pipe(
        finalize(() => {
          if (!handOffToPay && this.submitting) {
            this.submitting = false;
          }
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) return;
          const raw = res?.data ?? res;
          const orderId = this.resolveOrderId(raw);
          if (!orderId) return;
          this.writeLatestPointOrderId(orderId);
          if (!this.isPaymentPaid(raw)) {
            handOffToPay = true;
            this.payOrder(orderId);
            return;
          }
          this.openPaymentUrl(raw, orderId);
          this.modalRef.close({ refresh: true });
        },
        error: () => {}
      });
  }

  private loadBalanceSummary(): void {
    this.balanceCards = this.buildBalanceCards();
    this.http.post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_POINTS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) return;
        this.aipoolTenantId = String(this.aipoolTenantId || res?.data?.aipoolTenantId || '').trim();
        this.balanceCards = this.buildBalanceCards(res?.data);
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  continueUnpaidOrderPayment(): void {
    if (!this.hasUnpaidOrder) {
      return;
    }
    const orderId = this.resolveOrderId(this.unpaidOrder);
    if (!orderId) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-unpaid-order-missing'));
      return;
    }
    const paymentUrl = this.resolvePaymentUrl(this.unpaidOrder);
    if (paymentUrl && !this.isUnpaidOrderExpired) {
      this.openPaymentModal(paymentUrl, orderId);
      return;
    }
    this.submitting = true;
    this.payOrder(orderId);
  }

  cancelUnpaidOrder(): void {
    const orderId = this.resolveOrderId(this.unpaidOrder);
    if (!orderId) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-unpaid-order-missing'));
      return;
    }
    this.submitting = true;
    this.http.post(this.buildOrderPath(this.orderEndpoint('cancel'), orderId), {}, undefined, { silentAllErrors: true }).subscribe({
      next: res => {
        this.submitting = false;
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          this.message.warning(res?.msg || this.t('quota.view.point-package.modal.warning-cancel-unpaid-failed'));
          return;
        }
        this.clearUnpaidOrder();
        this.cdr.markForCheck();
      },
      error: err => {
        this.submitting = false;
        this.message.warning(err?.error?.msg || this.t('quota.view.point-package.modal.warning-cancel-unpaid-failed'));
      }
    });
  }

  triggerUnpaidOrderAction(): void {
    if (this.isUnpaidOrderExpired) {
      this.cancelUnpaidOrder();
      return;
    }
    this.continueUnpaidOrderPayment();
  }

  private loadExistingUnpaidOrder(): void {
    const pendingUrl = this.isNumericAddon
      ? `${this.orderEndpoint('pending')}?pricingCode=${encodeURIComponent(String(this.modalData.pricingCode ?? '').trim())}`
      : this.orderEndpoint('pending');
    this.http.post(pendingUrl, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          this.loadExistingUnpaidOrderFromSnapshot();
          return;
        }
        const raw = res?.data ?? null;
        const order = this.normalizeUnpaidOrder(raw);
        if (!order) {
          this.clearStoredLatestPointOrderId();
          return;
        }
        if (this.isOrderClosed(order)) {
          this.clearUnpaidOrder();
          return;
        }
        this.applyUnpaidOrder(order);
      },
      error: () => {
        this.loadExistingUnpaidOrderFromSnapshot();
      }
    });
  }

  private loadExistingUnpaidOrderFromSnapshot(): void {
    const orderId = this.readLatestPointOrderId();
    if (!orderId) {
      return;
    }
    const aipoolTenantId = String(this.aipoolTenantId || this.modalData.aipoolTenantId || '').trim();
    const payload: Record<string, string> = {};
    if (aipoolTenantId) {
      payload['aipoolTenantId'] = aipoolTenantId;
    }
    this.http.post(this.buildOrderPath(this.orderEndpoint('item'), orderId), payload).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          return;
        }
        const raw = res?.data ?? res;
        const order = this.normalizeUnpaidOrder(raw);
        if (!order) {
          this.clearStoredLatestPointOrderId();
          return;
        }
        if (this.isOrderClosed(order)) {
          this.clearUnpaidOrder();
          return;
        }
        this.applyUnpaidOrder(order);
      },
      error: () => {}
    });
  }

  private buildBalanceCards(raw?: any): BalanceCard[] {
    return [{ type: 'uupon', points: this.toNonNegativeInteger(raw?.uuponPoint), deductRate: 4 }];
  }

  private normalizeOptions(options?: Array<Partial<PointPackagePurchaseOption>> | null): PointPackagePurchaseOption[] {
    return (Array.isArray(options) ? options : [])
      .map((option, index) => ({
        pricingConfigId: String(option.pricingConfigId ?? '').trim(),
        pricingItemId: String(option.pricingItemId ?? (option as any)?.id ?? '').trim(),
        pricingCode: String(option.pricingCode ?? '').trim(),
        pricingType: String(option.pricingType ?? '').trim(),
        recommend: (option as any)?.recommend === true,
        title: String(
          option.title ??
            (option as any)?.name ??
            (option as any)?.pricingName ??
            option.description ??
            option.pricingCode ??
            option.pricingItemId ??
            `套餐 ${index + 1}`
        ).trim(),
        description: String(option.description ?? (option as any)?.subTitle ?? '').trim(),
        basePoints: this.toNonNegativeInteger(option.basePoints ?? (option as any)?.pointCount ?? (option as any)?.points),
        bonusPoints: this.toNonNegativeInteger(option.bonusPoints ?? (option as any)?.giftPoints),
        addonValue: this.toNonNegativeInteger((option as any)?.addonValue),
        displayOrder: this.toNonNegativeInteger(option.displayOrder ?? (option as any)?.sort ?? index + 1),
        priceCny: Number(option.priceCny ?? (option as any)?.discountPriceCny ?? 0) || 0
      }))
      .filter(option => !!option.pricingConfigId && !!option.pricingItemId)
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }

  private payOrder(orderId: string): void {
    this.http.post(this.buildOrderPath(this.orderEndpoint('pay'), orderId), this.buildPayPayload(orderId)).subscribe({
      next: res => {
        if (!res?.success) {
          this.submitting = false;
          return;
        }
        const payload = res?.data ?? res;
        this.storePaymentSnapshot(this.isNumericAddon ? 'quota-addon' : 'point', orderId, payload);
        this.writeLatestPointOrderId(orderId);
        this.submitting = false;
        this.openPaymentUrl(payload, orderId);
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  private buildPayPayload(orderId: string): Record<string, string> {
    const urls = this.buildPaymentReturnUrls(this.isNumericAddon ? 'quota-addon' : 'point', orderId);
    return {
      aipoolTenantId: this.aipoolTenantId,
      resultURL: urls.resultURL,
      cancelURL: urls.cancelURL
    };
  }

  private buildPaymentReturnUrls(orderType: PaymentOrderType, orderId: string): { resultURL: string; cancelURL: string } {
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    return {
      resultURL: buildPaymentResultUrl({
        orderType,
        paymentResult: 'success',
        tenantId,
        aipoolTenantId: this.aipoolTenantId,
        orderId
      }),
      cancelURL: buildPaymentResultUrl({
        orderType,
        paymentResult: 'failed',
        tenantId,
        aipoolTenantId: this.aipoolTenantId,
        orderId
      })
    };
  }

  private openPaymentUrl(raw?: any, orderId?: string): void {
    const payUrl = this.resolvePaymentUrl(raw);
    if (payUrl) {
      this.openPaymentModal(String(payUrl), orderId);
      return;
    }
    this.modalRef.close({ refresh: true });
  }

  private openPaymentModal(paymentUrl: string, orderId?: string): void {
    const paymentModal = this.modalService.create({
      nzContent: PaymentIframeModalComponent,
      nzWidth: '1200px',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'payment-iframe-modal',
      nzData: {
        paymentUrl,
        orderId,
        orderType: this.isNumericAddon ? 'quota-addon' : 'point'
      }
    });

    paymentModal.afterClose.subscribe(() => {
      this.modalRef.close({ refresh: true });
    });
  }

  private storePaymentSnapshot(orderType: PaymentOrderType, orderId: string, raw?: any): void {
    storePaymentSessionSnapshot({
      orderType,
      orderId,
      tenantId: String(this.modalData.tenantId ?? '').trim() || undefined,
      aipoolTenantId: this.aipoolTenantId || undefined,
      paymentUrl: this.resolvePaymentUrl(raw) || undefined,
      reserveTimeoutSeconds: this.toPositiveInteger(raw?.reserveTimeoutSeconds) || undefined,
      payStartTime: this.toPositiveInteger(raw?.payStartTime) || undefined,
      payExpireTime: this.toPositiveInteger(raw?.payExpireTime) || undefined,
      orderStatus: raw?.orderStatus ?? raw?.status,
      paymentStatus: raw?.paymentStatus,
      payRequired: this.isPayRequired(raw)
    });
  }

  private resolveOrderId(raw?: any): string {
    return String(raw?.orderId ?? raw?.id ?? '').trim();
  }

  private resolvePaymentUrl(raw?: any): string {
    return String(raw?.payUrl || raw?.paymentUrl || raw?.paymentLink || raw?.url || '').trim();
  }

  private normalizeUnpaidOrder(raw?: any): PointUnpaidOrderSnapshot | null {
    const orderId = this.resolveOrderId(raw);
    if (!orderId) {
      return null;
    }
    return {
      orderId,
      pricingConfigId: String(raw?.pricingConfigId ?? '').trim() || undefined,
      pricingItemId: String(raw?.pricingItemId ?? raw?.id ?? '').trim() || undefined,
      pricingCode: String(raw?.pricingCode ?? this.modalData.pricingCode ?? '').trim() || undefined,
      status: raw?.status,
      latestOrderStatus: raw?.latestOrderStatus,
      orderStatus: raw?.orderStatus,
      paymentStatus: raw?.paymentStatus,
      payableCash: Number(raw?.payableCash ?? raw?.priceCny ?? 0) || 0,
      priceCny: Number(raw?.priceCny ?? raw?.payableCash ?? 0) || 0,
      payStartTime: this.toPositiveInteger(raw?.payStartTime),
      payExpireTime: this.toPositiveInteger(raw?.payExpireTime),
      reserveTimeoutSeconds: this.toPositiveInteger(raw?.reserveTimeoutSeconds),
      paymentUrl: this.resolvePaymentUrl(raw) || undefined,
      needPay: raw?.needPay,
      payRequired: raw?.payRequired
    };
  }

  private applyUnpaidOrder(order: PointUnpaidOrderSnapshot): void {
    const matchedOption = this.options.find(option => option.pricingItemId === order.pricingItemId) ?? null;
    if (matchedOption) {
      this.selectedOption = matchedOption;
    }
    this.deductionInputs = { uupon: null };
    this.unpaidOrder = {
      ...order,
      paymentUrl: this.resolvePaymentUrl(order) || undefined
    };
    this.activeTab = 'unpaid';
    this.writeLatestPointOrderId(String(order.orderId ?? '').trim());
    this.cdr.markForCheck();
  }

  private clearUnpaidOrder(): void {
    this.unpaidOrder = null;
    this.activeTab = 'purchase';
    this.clearStoredLatestPointOrderId();
    this.cdr.markForCheck();
  }

  private isOrderClosed(order: PointUnpaidOrderSnapshot | null | undefined): boolean {
    if (!order) {
      return true;
    }
    const statusValues = [order.status, order.latestOrderStatus, order.orderStatus, order.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    return statusValues.some(status =>
      ['paid', 'success', 'succeeded', 'completed', 'complete', 'done', 'failed', 'canceled', 'cancelled', 'closed', 'replaced'].includes(
        status
      )
    );
  }

  private getUnpaidOrderRemainingMs(): number | null {
    return getPaymentRemainingMs(this.unpaidOrder);
  }

  private getLatestPointOrderStorageKey(): string {
    const tenantId = String(this.modalData.tenantId ?? '').trim() || 'anonymous';
    const pricingCode = String(this.modalData.pricingCode ?? '').trim() || 'POINT_LIMIT';
    return `point-package:latest-order:${tenantId}:${pricingCode}`;
  }

  private formatTimestamp(value: number | null | undefined): string {
    const timestamp = Number(value ?? 0);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      return '--';
    }
    return new Intl.DateTimeFormat(this.i18n.currentLang || undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(timestamp);
  }

  private readLatestPointOrderId(): string | null {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    const orderId = String(window.sessionStorage.getItem(this.getLatestPointOrderStorageKey()) ?? '').trim();
    if (orderId) {
      const snapshot = readPaymentSessionSnapshot('point', orderId);
      if (!snapshot || snapshot.orderType !== 'point') {
        return null;
      }
    }
    return orderId || null;
  }

  private writeLatestPointOrderId(orderId: string): void {
    if (!orderId || typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    window.sessionStorage.setItem(this.getLatestPointOrderStorageKey(), orderId);
  }

  private clearStoredLatestPointOrderId(): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    window.sessionStorage.removeItem(this.getLatestPointOrderStorageKey());
  }

  private isPayRequired(raw?: any): boolean {
    return raw?.payRequired === true || raw?.needPay === true || !!raw?.paymentRequired;
  }

  private isPaymentPaid(raw?: any): boolean {
    const statusValues = [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    if (statusValues.some(status => ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status))) {
      return true;
    }
    if (Number(raw?.status) === 30 || Number(raw?.latestOrderStatus ?? raw?.orderStatus) === 30 || Number(raw?.paymentStatus) === 20) {
      return true;
    }
    return raw?.needPay === false || raw?.payRequired === false;
  }

  private normalizeUUPonMultiple(points: number): number | null {
    const normalized = Math.floor(Number(points) || 0);
    const multiple = normalized - (normalized % 4);
    return multiple > 0 ? multiple : null;
  }

  private validateUUPonMultiple(): boolean {
    const points = this.getDeductPoints('uupon');
    if (points > 0 && points % 4 !== 0) {
      this.message.warning(this.t('quota.view.point-package.modal.warning-deduct-multiple'));
      return false;
    }
    return true;
  }

  private toNonNegativeInteger(value: unknown): number {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.floor(parsed);
  }

  private toPositiveInteger(value: unknown): number | null {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.floor(parsed);
  }

  private buildOrderPath(template: string, orderId: string): string {
    return template.replace('{orderId}', encodeURIComponent(orderId));
  }
}
